import { RefObject, useEffect, useState } from 'react';

/**
 * True when the space AVAILABLE to the referenced element is narrower than `enterBelow`, so a
 * component can switch to a stacked layout based on the room it has rather than the viewport size.
 *
 * It observes the element's PARENT, not the element. Measuring the element itself is wrong for
 * anything content-sized: a CustomToggle labelled "Blind" is only ~114px wide on a 1440px screen,
 * so it would report itself as cramped and collapse on desktop. The parent's width is the actual
 * constraint.
 *
 * Why not a CSS container query: `container-type: inline-size` also applies inline-size
 * containment, which makes an element's width independent of its contents. Both callers here
 * (MonetaryInput, CustomToggle) size themselves with `width: fit-content`, so becoming a query
 * container would collapse them. A ResizeObserver measures the same thing without that side effect.
 *
 * Deliberately NOT gated on viewport width. A control squeezed into a narrow column is just as
 * unreadable on a 1400px screen as on a phone, so the trigger is the space the control actually
 * has. Thresholds must therefore stay below any narrow-but-intentional desktop column -- notably
 * the Budget expense rows, which give the amount a fixed 180px.
 *
 * Hysteresis is kept even so: a stacked child can change its parent's height and, in some
 * layouts, its width. Switching to compact below `enterBelow` but back only above `exitAbove`
 * gives the state somewhere stable to settle. Keep a gap of at least ~20px between the two.
 */
export function useCompactLayout(
    ref: RefObject<HTMLElement | null>,
    enterBelow: number,
    exitAbove: number
): boolean {
    const [isCompact, setIsCompact] = useState(false);

    useEffect(() => {
        const element = ref.current?.parentElement;
        if (!element || typeof ResizeObserver === 'undefined') {
            return;
        }

        const measure = () => {
            const width = element.getBoundingClientRect().width;
            setIsCompact((previous) => (previous ? width <= exitAbove : width < enterBelow));
        };

        const observer = new ResizeObserver(measure);
        observer.observe(element);
        measure();

        return () => observer.disconnect();
    }, [ref, enterBelow, exitAbove]);

    return isCompact;
}
